import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Alert,
  Divider,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import TankIcon from '@mui/icons-material/Water';
import TimelineIcon from '@mui/icons-material/Timeline';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import ProductSetupWizard from './prediction/wizards/ProductSetupWizard';

const FunctionControlDialog = ({
  open,
  onClose,
  selectedProduct,
  onUpdate,
  onShowMessage
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(false);
  const [tankInstalled, setTankInstalled] = useState(false);
  const [predictionEnabled, setPredictionEnabled] = useState(false);
  const [showPredictionOffConfirm, setShowPredictionOffConfirm] = useState(false);
  const [setupWizardOpen, setSetupWizardOpen] = useState(false);
  
  useEffect(() => {
    if (open && selectedProduct?.id) {
      const initialTankInstalled = Boolean(selectedProduct.tankInstalled);
      const initialPredictionEnabled = Boolean(selectedProduct.predictionEnabled);
      
      setTankInstalled(initialTankInstalled);
      setPredictionEnabled(initialPredictionEnabled);
      setShowPredictionOffConfirm(false);
      setSetupWizardOpen(false);
    }
  }, [open, selectedProduct]);

  if (!selectedProduct?.id) {
    return null;
  }

  const getServiceMode = (tank, prediction) => {
    if (tank && prediction) return 'PREDICTION_AUTO';
    if (tank && !prediction) return 'TANK_MANUAL';
    if (!tank && prediction) return 'PREDICTION_ONLY';
    return 'PRICING_ONLY';
  };

  const currentMode = getServiceMode(tankInstalled, predictionEnabled);
  const originalMode = getServiceMode(
    Boolean(selectedProduct?.tankInstalled), 
    Boolean(selectedProduct?.predictionEnabled)
  );

  const modeDescriptions = {
    'PREDICTION_AUTO': {
      icon: <AutoAwesomeIcon color="success" />,
      label: 'フル機能',
      color: 'success',
      description: 'タンク管理と自動予測の両方を利用',
      features: ['タンク容量・残量管理', '自動予測計算', '割合表示', '補充アラート']
    },
    'TANK_MANUAL': {
      icon: <TankIcon color="primary" />,
      label: 'タンク管理',
      color: 'primary', 
      description: 'タンク設置済みだが予測機能未使用',
      features: ['タンク容量・残量管理', '割合表示']
    },
    'PREDICTION_ONLY': {
      icon: <TimelineIcon color="info" />,
      label: '予測機能',
      color: 'info',
      description: 'タンク未設置だが予測機能は利用',
      features: ['消費量ベース予測', '実測値からの計算', '絶対値での管理']
    },
    'PRICING_ONLY': {
      icon: <MonetizationOnIcon color="warning" />,
      label: '基本モード',
      color: 'warning',
      description: '単価管理のみ利用',
      features: ['単価設定・管理', 'シンプルな操作']
    }
  };

  const hasChanges = currentMode !== originalMode;

  const handleConfirmPredictionOff = async () => {
    setShowPredictionOffConfirm(false);
    setLoading(true);
    
    try {
      const token = sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.put(
        `/api/client-products/${selectedProduct.id}/service-mode?mode=${currentMode}`,
        {},
        { headers }
      );

      const verifyResponse = await axios.get(`/api/client-products/${selectedProduct.id}`, { headers });

      alert(`機能設定を「${modeDescriptions[currentMode].label}」に変更しました`);
      
      onClose();
      
      if (onUpdate) {
        onUpdate();
      }
      
    } catch (error) {
      const message = error.response?.data || '機能設定の更新に失敗しました';
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupComplete = async (setupData) => {
    try {
      const token = sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const updatePayload = {
        clientId: selectedProduct.clientId,
        productId: selectedProduct.productId,
        
        ...(setupData.totalCapacity && {
          totalCapacity: setupData.totalCapacity
        }),
        
        ...(setupData.initialConsumptionRate && {
          initialConsumptionRate: setupData.initialConsumptionRate
        }),
        
        ...(setupData.actualRemaining !== undefined && 
           setupData.actualRemaining !== null && 
           setupData.actualRemaining !== '' && {
          actualRemaining: parseInt(setupData.actualRemaining, 10),
          measuredDate: setupData.measuredDate || new Date().toISOString().split('T')[0]
        })
      };

      // Step 1: 製品設定を更新
      await axios.put(`/api/client-products/${selectedProduct.id}`, updatePayload, { headers });

      // Step 2: サービスモードを決定
      const currentTankEnabled = Boolean(tankInstalled === true);
      const currentPredictionEnabled = Boolean(predictionEnabled === true);
      
      let newMode;
      if (currentTankEnabled && currentPredictionEnabled) {
        newMode = 'PREDICTION_AUTO';
      } else if (currentTankEnabled && !currentPredictionEnabled) {
        newMode = 'TANK_MANUAL';  
      } else if (!currentTankEnabled && currentPredictionEnabled) {
        newMode = 'PREDICTION_ONLY';
      } else {
        newMode = 'PRICING_ONLY';
      }

      // Step 3: サービスモード設定
      const serviceModeResponse = await axios.put(
        `/api/client-products/${selectedProduct.id}/service-mode?mode=${newMode}`,
        {},
        { headers }
      );

      const verifyResponse = await axios.get(`/api/client-products/${selectedProduct.id}`, { headers });

      alert(`製品設定が完了し、${modeDescriptions[newMode].label}が有効になりました`);
      
      onUpdate?.();
      setSetupWizardOpen(false);
      
    } catch (error) {
      alert('設定に失敗しました: ' + (error.response?.data || error.message));
    }
  };

  const handleSave = async () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    const originalPredictionEnabled = Boolean(selectedProduct?.predictionEnabled);
    const newPredictionEnabled = predictionEnabled;

    if (!originalPredictionEnabled && newPredictionEnabled) {
      setSetupWizardOpen(true);
      onClose();
      return;
    }

    if (originalPredictionEnabled && !newPredictionEnabled) {
      setShowPredictionOffConfirm(true);
      return;
    }

    setLoading(true);
    
    try {
      const token = sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.put(
        `/api/client-products/${selectedProduct.id}/service-mode?mode=${currentMode}`,
        {},
        { headers }
      );

      const verifyResponse = await axios.get(`/api/client-products/${selectedProduct.id}`, { headers });

      alert(`機能設定を「${modeDescriptions[currentMode].label}」に変更しました`);
      
      onClose();
      
      if (onUpdate) {
        onUpdate();
      }
      
    } catch (error) {
      const message = error.response?.data || '機能設定の更新に失敗しました';
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const currentModeConfig = modeDescriptions[currentMode];

  if (showPredictionOffConfirm) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            <Typography variant="h6">予測機能を無効にしますか？</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>注意：以下のデータ・機能が利用できなくなります</strong>
            </Typography>
          </Alert>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              利用できなくなる機能：
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
              <Typography variant="body2">• 自動残量予測</Typography>
              <Typography variant="body2">• 実測残量の更新</Typography>
              <Typography variant="body2">• 消費量ベースの計算</Typography>
              <Typography variant="body2">• 補充アラート（容量設定時）</Typography>
            </Box>
          </Box>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>ヒント：</strong>後から再度有効にできますが、
              消費量などの設定をやり直す必要があります。
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 1.5 }}>
          <Button 
            variant="contained" 
            color="warning"
            onClick={handleConfirmPredictionOff}
            disabled={loading}
            size="large"
            fullWidth
            sx={{ order: 1 }}
          >
            {loading ? '処理中...' : '予測機能を無効にする'}
          </Button>
          
          <Button 
            variant="text"
            onClick={() => setShowPredictionOffConfirm(false)} 
            disabled={loading}
            color="inherit"
            size="medium"
            sx={{ order: 2, minWidth: 100 }}
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle component="div" disableTypography>
          <Typography variant="h6" fontWeight="bold">
            機能設定の変更
          </Typography>
          <Typography variant="body2" color="text.secondary">
            タンク設置状況と予測機能の利用状況を設定してください
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>現在の設定:</strong> {modeDescriptions[originalMode].label}
            </Typography>
          </Alert>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              機能の有効・無効
            </Typography>
            
            <Card variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={tankInstalled}
                        onChange={(e) => {
                          setTankInstalled(e.target.checked);
                        }}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TankIcon />
                        <Typography>タンクを設置している</Typography>
                      </Box>
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                    顧客先にタンクが設置されている場合はONにしてください
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={predictionEnabled}
                        onChange={(e) => {
                          setPredictionEnabled(e.target.checked);
                        }}
                        color="info"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TimelineIcon />
                        <Typography>予測機能を使用する</Typography>
                      </Box>
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                    消費量に基づく残量予測を利用する場合はONにしてください
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              設定後のモード
            </Typography>
            
            <Card 
              variant="outlined" 
              sx={{ 
                borderColor: `${currentModeConfig.color}.main`,
                bgcolor: `${currentModeConfig.color}.50`
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {currentModeConfig.icon}
                  <Chip 
                    label={currentModeConfig.label}
                    color={currentModeConfig.color}
                    variant="filled"
                  />
                  {hasChanges && (
                    <Chip 
                      label="変更あり"
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {currentModeConfig.description}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" fontWeight="medium">
                    利用可能な機能:
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {currentModeConfig.features.map((feature, index) => (
                    <Typography 
                      key={index}
                      variant="caption" 
                      sx={{ 
                        bgcolor: 'background.paper',
                        px: 1,
                        py: 0.25,
                        borderRadius: 0.5,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      {feature}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {currentMode === 'PRICING_ONLY' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              基本モードでは残量予測やタンク管理機能は利用できません。
            </Alert>
          )}
          
          {currentMode === 'PREDICTION_ONLY' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              予測機能のみモードでは、容量に対する割合表示は利用できません。
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 1.5 }}>
          <Button 
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            color={hasChanges ? 'primary' : 'inherit'}
            size="large"
            fullWidth
            sx={{ order: 1 }}
          >
            {loading ? '保存中...' : hasChanges ? '変更を保存' : '閉じる'}
          </Button>
          
          <Button 
            variant="text"
            onClick={onClose} 
            disabled={loading}
            color="inherit"
            size="medium"
            sx={{ order: 2, minWidth: 100 }}
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      {setupWizardOpen && selectedProduct?.id && (
        <ProductSetupWizard
          open={setupWizardOpen}
          onClose={() => {
            setSetupWizardOpen(false);
          }}
          onComplete={handleSetupComplete}
          productName={selectedProduct?.productName || ''}
          clientId={selectedProduct?.clientId}
          productId={selectedProduct?.productId}
          selectedProduct={{ 
            ...selectedProduct, 
            tankInstalled: tankInstalled
          }}
          showTankSetupStep={false}
        />
      )}
    </>
  );
};

export default FunctionControlDialog;